import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  hasModule,
  canViewModule,
  canAccessAnalisisCrediticio,
  canAccessDetalleCaso,
  canAccessControlCasos,
  canAccessBusquedaCasos,
  canAccessTrackingEventos,
  canAccessCesionesIniciar,
  canAccessCesionesControl,
  canAccessCesionesAprobacion,
  canAccessCesionesBusqueda,
  canAccessCesionesTracking,
  canAccessCesionesRevision,
  canAccessPrendasDisponibles,
  canAccessPrendasControl,
  canAccessPrendasEvaluacion,
  canAccessPrendasBusqueda,
  canAccessPrendasTracking,
} from '../../utils/auth';

const MenuNavigator = ({ 
  firstName,
  lastName,
  userAvatar, 
  userRole, 
  onHelp, 
  onSettings, 
  onLogout,
  onHome,
  onBack,
  onNavigate,
  showBackButton = false,
  showHomeButton = true,
  isHomePage = false,
  userGroupname // Mantener por compatibilidad, pero ya no se usa para permisos
}) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isScoringDropdownOpen, setIsScoringDropdownOpen] = useState(false);
  const [isCesionesDropdownOpen, setIsCesionesDropdownOpen] = useState(false);
  const [isPrendasDropdownOpen, setIsPrendasDropdownOpen] = useState(false);
  const scoringDropdownRef = useRef(null);
  const cesionesDropdownRef = useRef(null);
  const prendasDropdownRef = useRef(null);
  const location = useLocation();

  // Datos de casos simulados (en producción vendría de una API)
  const casosData = [
    { id: 1, estado: 'incompleto' },
    { id: 2, estado: 'disponible' },
    { id: 3, estado: 'disponible' },
    { id: 4, estado: 'rechazado' },
    { id: 5, estado: 'incompleto' },
    { id: 6, estado: 'rechazado' },
    { id: 7, estado: 'rechazado' },
    { id: 8, estado: 'rechazado' },
  ];

  // Calcular totales de casos que requieren atención
  const cantidadRechazados = casosData.filter(caso => caso.estado === 'rechazado').length;
  const cantidadIncompletos = casosData.filter(caso => caso.estado === 'incompleto').length;
  const totalCasosConAtencion = cantidadRechazados + cantidadIncompletos;

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scoringDropdownRef.current && !scoringDropdownRef.current.contains(event.target)) {
        setIsScoringDropdownOpen(false);
      }
      if (cesionesDropdownRef.current && !cesionesDropdownRef.current.contains(event.target)) {
        setIsCesionesDropdownOpen(false);
      }
      if (prendasDropdownRef.current && !prendasDropdownRef.current.contains(event.target)) {
        setIsPrendasDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para obtener las iniciales del nombre y apellido
  const getInitials = (first, last) => {
    if (!first && !last) return 'U';
    if (first && last) {
      // Primera letra del nombre y primera letra del apellido
      return (first[0] + last[0]).toUpperCase();
    }
    // Si solo hay nombre, tomar la primera letra o las dos primeras
    const name = first || last;
    return name.substring(0, Math.min(2, name.length)).toUpperCase();
  };

  const handleModuleClick = (action) => {
    if (onNavigate) {
      onNavigate(action);
    }
  };

  const handleHelpClick = () => {
    setIsHelpModalOpen(true);
    if (onHelp) onHelp();
  };

  // Lista de documentos de ayuda
  const helpDocuments = [
    { id: 1, name: 'Manual de Usuario - Sistema de Cesiones', category: 'Manual', icon: 'description', size: '2.4 MB' },
    { id: 2, name: 'Guía Rápida - Gestión de Prendas', category: 'Guía', icon: 'menu_book', size: '1.8 MB' },
    { id: 3, name: 'Manual de Scoring Crediticio', category: 'Manual', icon: 'analytics', size: '3.1 MB' },
    { id: 4, name: 'Políticas de Seguridad y Buenas Prácticas', category: 'Documento', icon: 'security', size: '1.2 MB' },
    { id: 5, name: 'FAQ - Preguntas Frecuentes', category: 'FAQ', icon: 'help', size: '0.9 MB' },
    { id: 6, name: 'Tutorial de Importación de Datos', category: 'Tutorial', icon: 'upload_file', size: '2.1 MB' },
    { id: 7, name: 'Formularios y Plantillas Oficiales', category: 'Plantilla', icon: 'assignment', size: '1.5 MB' },
    { id: 8, name: 'Manual de Administración', category: 'Manual', icon: 'admin_panel_settings', size: '3.4 MB' }
  ];

  const handleDownload = (doc) => {
    alert(`Descargando: ${doc.name}\nTamaño: ${doc.size}\nEsta funcionalidad se conectará con el servidor de archivos.`);
  };

  // Módulos para el header central - Filtrados por permisos del usuario
  const allModules = [
    { id: 1, title: 'Cesiones', icon: 'autorenew', action: 'cesionesDashboard', description: 'Transferencias de vehículos', hasDropdown: true, requiredModule: 'CESIONES' },
    { id: 2, title: 'Prendas', icon: 'verified', action: 'prendasDashboard', description: 'Créditos prendarios', hasDropdown: true, requiredModule: 'PRENDAS' },
    { id: 3, title: 'Scoring', icon: 'analytics', action: 'scoringDashboard', description: 'Análisis crediticio', hasDropdown: true, requiredModule: 'SCORING' },
    { id: 4, title: 'ABM', icon: 'group', action: 'abmUsuarios', description: 'Administración de usuarios', requiredModule: 'ABM' }
  ];

  // Submenú del módulo Scoring - Filtrado por permisos de grupo
  // CONCESIONARIO: Análisis Disponible, Control de Casos, Búsqueda, Tracking
  // HONDA: Detalle del Caso, Búsqueda, Tracking
  // HONDA: Análisis de Casos, Búsqueda, Tracking
  const allScoringModules = [
    { 
      id: 2, 
      title: 'Análisis Disponible', 
      icon: 'assignment_turned_in', 
      action: 'analisisCrediticioDisponible', 
      description: 'Revisar análisis crediticios pendientes', 
      route: '/analisis-crediticio-disponible',
      canAccess: canAccessAnalisisCrediticio
    },
    { 
      id: 3, 
      title: 'Análisis de Casos', 
      icon: 'description', 
      action: 'analisisDeCasos', 
      description: 'Validar información del caso', 
      route: '/analisis-de-casos',
      canAccess: canAccessDetalleCaso
    },
    { 
      id: 4, 
      title: 'Control de Casos', 
      icon: 'assignment', 
      action: 'casos', 
      description: 'Gestionar casos', 
      route: '/casos',
      canAccess: canAccessControlCasos
    },
    { 
      id: 5, 
      title: 'Búsqueda de Casos', 
      icon: 'search', 
      action: 'busquedaCasos', 
      description: 'Buscar casos por criterios', 
      route: '/busqueda-casos',
      canAccess: canAccessBusquedaCasos
    },
    { 
      id: 6, 
      title: 'Tracking de Eventos', 
      icon: 'history', 
      action: 'trackingEventos', 
      description: 'Seguimiento de actividades', 
      route: '/tracking-eventos',
      canAccess: canAccessTrackingEventos
    }
  ];

  // Filtrar módulos del header según permisos del usuario
  const getFilteredModules = () => {
    return allModules.filter(module => {
      // Si el módulo no requiere permisos específicos, mostrarlo
      if (!module.requiredModule) return true;
      // Si requiere un módulo específico, verificar si el usuario lo tiene
      // canViewModule automáticamente da acceso total a usuarios Xerox
      return canViewModule(module.requiredModule);
    });
  };

  // Filtrar submenú de Scoring según grupo del usuario
  const getScoringSubmenu = () => {
    if (!canViewModule('SCORING')) return [];
    return allScoringModules.filter(item => item.canAccess && item.canAccess());
  };

  // Submenú de Cesiones - filtrado por permisos de grupo
  const allCesionesModules = [
    { id: 1, title: 'Iniciar Cesión', icon: 'autorenew', action: 'iniciarCesion', description: 'Iniciar nueva cesión', route: '/iniciar-cesion', canAccess: canAccessCesionesIniciar },
    { id: 2, title: 'Control de Cesiones', icon: 'assignment', action: 'controlCesiones', description: 'Gestionar cesiones rechazadas', route: '/control-cesiones', canAccess: canAccessCesionesControl },
    { id: 3, title: 'Revisión de Cesiones', icon: 'pending_actions', action: 'revisionCesion', description: 'Cesiones pendientes de aprobación', route: '/revision-cesion', canAccess: canAccessCesionesRevision },
    { id: 4, title: 'Búsqueda de Cesiones', icon: 'search', action: 'busquedaCesiones', description: 'Buscar cesiones', route: '/busqueda-cesiones', canAccess: canAccessCesionesBusqueda },
    { id: 5, title: 'Tracking de Cesiones', icon: 'history', action: 'trackingCesiones', description: 'Seguimiento de cesiones', route: '/tracking-cesiones', canAccess: canAccessCesionesTracking },
  ];

  const getCesionesSubmenu = () => {
    if (!canViewModule('CESIONES')) return [];
    return allCesionesModules.filter(item => item.canAccess && item.canAccess());
  };

  // Submenú de Prendas - filtrado por permisos de grupo
  const allPrendasModules = [
    { id: 1, title: 'Prendas Disponibles', icon: 'inventory_2', action: 'prendasDisponibles', description: 'Prendas disponibles para registro', route: '/prendas-disponibles', canAccess: canAccessPrendasDisponibles },
    { id: 2, title: 'Evaluación de Prendas', icon: 'fact_check', action: 'evaluarPrenda', description: 'Evaluar prendas en revisión', route: '/evaluar-prenda', canAccess: canAccessPrendasEvaluacion },
    { id: 3, title: 'Control de Prendas', icon: 'assignment', action: 'controlPrendas', description: 'Gestionar prendas incompletas', route: '/control-prendas', canAccess: canAccessPrendasControl },
    { id: 4, title: 'Búsqueda de Prendas', icon: 'search', action: 'busquedaPrendas', description: 'Buscar prendas por grupo/orden', route: '/busqueda-prendas', canAccess: canAccessPrendasBusqueda },
    { id: 5, title: 'Tracking de Prendas', icon: 'history', action: 'trackingPrendas', description: 'Historial de eventos de prendas', route: '/tracking-prendas', canAccess: canAccessPrendasTracking },
  ];

  const getPrendasSubmenu = () => {
    if (!canViewModule('PRENDAS')) return [];
    return allPrendasModules.filter(item => item.canAccess && item.canAccess());
  };

  const scoringSubmenu = getScoringSubmenu();
  const cesionesSubmenu = getCesionesSubmenu();
  const prendasSubmenu = getPrendasSubmenu();
  const headerModules = getFilteredModules();

  return (
    <>
      <div className="w-full h-full pl-0 pr-4 py-2">
        <div className="relative flex items-center justify-between h-full">
          {/* Logo de Honda y botones de navegación izquierda */}
          <div className="flex items-center gap-1 z-10">
            {/* Logo de Honda */}
            <img 
              src="/Hondalogo.png" 
              alt="Honda Logo" 
              className="h-12 w-auto object-contain brightness-0 invert drop-shadow-lg"
            />
            
            {/* Separador decorativo */}
            <div className="w-px h-8 bg-white/30 shadow-sm"></div>
            
            {showHomeButton && (
              <button
                onClick={onHome}
                className="group w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 hover:shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 backdrop-blur-sm"
                title="Principal"
              >
                <span className="material-icons text-white text-xl group-hover:scale-110 transition-transform">home</span>
              </button>
            )}
            
            {showBackButton && (
              <button
                onClick={onBack}
                className="group w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 hover:shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 backdrop-blur-sm"
                title="Volver"
              >
                <span className="material-icons text-white text-xl group-hover:-translate-x-1 transition-transform">arrow_back</span>
              </button>
            )}
          </div>

          {/* Módulos centrales - Posicionados absolutamente en el centro */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            {!isHomePage && headerModules.length > 0 && (
              <div className="flex items-center gap-8">
                {headerModules.map((module) => (
                  module.hasDropdown ? (
                    // Dropdown para módulos con submenú (Cesiones / Prendas / Scoring)
                    <div
                      key={module.id}
                      className="relative"
                      ref={module.id === 1 ? cesionesDropdownRef : module.id === 2 ? prendasDropdownRef : scoringDropdownRef}
                    >
                      <button
                        onClick={() => {
                          if (module.id === 1) {
                            setIsCesionesDropdownOpen(!isCesionesDropdownOpen);
                            setIsScoringDropdownOpen(false);
                            setIsPrendasDropdownOpen(false);
                          } else if (module.id === 2) {
                            setIsPrendasDropdownOpen(!isPrendasDropdownOpen);
                            setIsCesionesDropdownOpen(false);
                            setIsScoringDropdownOpen(false);
                          } else {
                            setIsScoringDropdownOpen(!isScoringDropdownOpen);
                            setIsCesionesDropdownOpen(false);
                            setIsPrendasDropdownOpen(false);
                          }
                        }}
                        className="group flex flex-col items-center gap-1.5 transition-all duration-300 hover:scale-110"
                        title={module.description}
                      >
                        <div className="relative">
                          <div className="absolute inset-0 bg-white/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="relative w-10 h-10 rounded-xl bg-white/10 group-hover:bg-white/20 backdrop-blur-sm transition-all duration-300 flex items-center justify-center group-hover:shadow-lg">
                            <span className="material-icons text-white text-[22px] leading-none group-hover:scale-110 transition-transform">
                              {module.icon}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-white/70 text-[11px] font-semibold group-hover:text-white transition-colors whitespace-nowrap">
                            {module.title}
                          </span>
                          <span className="material-icons text-white/70 text-[14px] group-hover:text-white transition-all">
                            {(module.id === 1 ? isCesionesDropdownOpen : module.id === 2 ? isPrendasDropdownOpen : isScoringDropdownOpen) ? 'expand_less' : 'expand_more'}
                          </span>
                        </div>
                      </button>

                      {/* Dropdown Menu */}
                      {((module.id === 1 && isCesionesDropdownOpen) || (module.id === 2 && isPrendasDropdownOpen) || (module.id === 3 && isScoringDropdownOpen)) && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-2xl overflow-hidden z-[100] border border-gray-200">
                          <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-2">
                            <p className="text-white text-sm font-bold">
                              {module.id === 1 ? 'Módulo de Cesiones' : module.id === 2 ? 'Módulo de Prendas' : 'Módulo de Scoring'}
                            </p>
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            {(module.id === 1 ? cesionesSubmenu : module.id === 2 ? prendasSubmenu : scoringSubmenu).map((item) => {
                              const isCurrentPage = location.pathname === item.route;
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    if (!isCurrentPage) {
                                      handleModuleClick(item.action);
                                      setIsScoringDropdownOpen(false);
                                      setIsCesionesDropdownOpen(false);
                                      setIsPrendasDropdownOpen(false);
                                    }
                                  }}
                                  className={`w-full py-3 flex items-center gap-3 transition-colors text-left border-b border-gray-100 last:border-b-0 relative ${
                                    isCurrentPage ? 'bg-red-50 cursor-default' : 'hover:bg-gray-50 cursor-pointer'
                                  }`}
                                  title={isCurrentPage ? 'Página actual' : item.description}
                                  disabled={isCurrentPage}
                                >
                                  {isCurrentPage && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-red-600 to-red-700"></div>
                                  )}
                                  <div className={`ml-4 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isCurrentPage ? 'bg-red-100' : 'bg-red-50'}`}>
                                    <span className={`material-icons text-[18px] ${isCurrentPage ? 'text-red-700' : 'text-red-600'}`}>
                                      {item.icon}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${isCurrentPage ? 'text-red-700 font-bold' : 'text-gray-800 font-medium'}`}>{item.title}</p>
                                    <p className={`text-xs ${isCurrentPage ? 'text-red-600' : 'text-gray-500'}`}>{item.description}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Botón normal para otros módulos
                    <button
                      key={module.id}
                      onClick={() => handleModuleClick(module.action)}
                      className="group flex flex-col items-center gap-1.5 transition-all duration-300 hover:scale-110"
                      title={module.description}
                    >
                      <div className="relative">
                        <div className="absolute inset-0 bg-white/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative w-10 h-10 rounded-xl bg-white/10 group-hover:bg-white/20 backdrop-blur-sm transition-all duration-300 flex items-center justify-center group-hover:shadow-lg">
                          <span className="material-icons text-white text-[22px] leading-none group-hover:scale-110 transition-transform">
                            {module.icon}
                          </span>
                        </div>
                      </div>
                      <span className="text-white/70 text-[11px] font-semibold group-hover:text-white transition-colors whitespace-nowrap">
                        {module.title}
                      </span>
                    </button>
                  )
                ))}
              </div>
            )}
          </div>
          
          {/* Acciones y perfil mejorados */}
          <div className="flex items-center gap-4 z-10">
            {/* Botón de ayuda mejorado */}
            <button
              onClick={handleHelpClick}
              className="relative group w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 hover:shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 backdrop-blur-sm"
            >
              <span className="material-icons text-white text-xl group-hover:rotate-12 transition-transform">help_outline</span>
              <span className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 invisible group-hover:visible transition-all duration-300 text-xs text-white bg-gray-900 px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg pointer-events-none">
                Ayuda
              </span>
            </button>
            
            {/* Separador decorativo */}
            <div className="w-px h-8 bg-white/30 shadow-sm"></div>
            
            {/* Perfil de usuario mejorado */}
            <div className="flex items-center gap-3 pl-1">
              {/* Avatar con iniciales */}
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg ring-2 ring-white/20">
                  {userAvatar ? (
                    <img src={userAvatar} alt={`${firstName} ${lastName}`} className="w-full h-full rounded-xl object-cover" />
                  ) : (
                    <span className="text-white text-sm font-bold">{getInitials(firstName, lastName)}</span>
                  )}
                </div>
              </div>
              
              {/* Info usuario */}
              <div className="text-left">
                <p className="text-white text-sm font-bold drop-shadow-md leading-tight">{firstName}</p>
                {lastName && <p className="text-white text-sm font-bold drop-shadow-md leading-tight">{lastName}</p>}
              </div>
              
              {/* Botón de cerrar sesión mejorado */}
              <button
                onClick={onLogout}
                className="ml-2 w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 hover:shadow-lg transition-all duration-300 flex items-center justify-center group relative hover:scale-110 backdrop-blur-sm"
              >
                <span className="material-icons text-white text-lg group-hover:rotate-180 transition-transform duration-500">logout</span>
                <span className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 invisible group-hover:visible transition-all duration-300 text-xs text-white bg-gray-900 px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none shadow-lg">
                  Cerrar Sesión
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Ayuda - Librería de Documentos */}
      {isHelpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsHelpModalOpen(false)}
          ></div>
          
          {/* Modal */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden animate-scale-in">
              {/* Header del modal con gradiente */}
              <div className="gradient-red px-6 py-5 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <span className="material-icons text-white text-2xl">local_library</span>
                  </div>
                  <h2 className="text-white text-xl font-bold">Ayuda</h2>
                </div>
                <div className="flex items-center gap-2">
                  {/* Botón cerrar */}
                  <button
                    onClick={() => setIsHelpModalOpen(false)}
                    className="text-white/80 hover:text-white hover:rotate-90 transition-all duration-300 w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10"
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>
              </div>
              
              {/* Body del modal */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] scrollbar-thin">
                {/* VISTA DE LIBRERÍA - PDFs */}
                <div className="mb-6 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                  <div className="flex items-start gap-3">
                    <span className="material-icons text-blue-500 text-2xl">info</span>
                    <div>
                      <h3 className="text-blue-900 font-bold mb-1">Librería de Documentos</h3>
                      <p className="text-blue-700 text-sm">
                        Descarga manuales, guías y documentación oficial del sistema.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {helpDocuments.map((doc, index) => (
                    <div
                      key={doc.id}
                      onClick={() => handleDownload(doc)}
                      className="group flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-white hover:from-honda-50 hover:to-white rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg border-2 border-gray-100 hover:border-honda-200"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="relative">
                          <div className="absolute inset-0 bg-honda-100 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-honda-50 to-honda-100 group-hover:from-honda-100 group-hover:to-honda-200 transition-all duration-300 flex items-center justify-center shadow-soft group-hover:scale-110">
                            <span className="material-icons text-honda-600 text-2xl">
                              {doc.icon}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <h3 className="text-gray-800 font-bold group-hover:text-honda-700 transition-colors text-sm">
                              {doc.name}
                            </h3>
                            <span className="px-2.5 py-1 bg-gradient-to-r from-honda-100 to-honda-200 text-honda-700 text-xs rounded-full font-semibold">
                              {doc.category}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-gray-500 text-xs flex items-center gap-1">
                              <span className="material-icons text-xs">description</span>
                              {doc.size}
                            </p>
                            <p className="text-gray-500 text-xs flex items-center gap-1">
                              <span className="material-icons text-xs">picture_as_pdf</span>
                              PDF
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <button className="ml-4 px-5 py-2.5 gradient-red hover:shadow-glow-red text-white text-sm rounded-xl transition-all duration-300 flex items-center gap-2 font-semibold">
                        <span className="material-icons text-base">download</span>
                        Descargar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estilos CSS para la animación */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default MenuNavigator;
